import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { fetchAppContext } from '../services/api';
import { useProfileStore } from './profileStore';
import { useInventoryStore } from './inventoryStore';
import { useWineStore } from './wineStore';
import { useMacroStore } from './macroStore';
import { useMealPlanStore } from './mealPlanStore';
import { ref, onValue, off } from 'firebase/database';
import { database } from '../firebase-config';
import { UserTier, isValidVarietyMode } from '../types';

interface AppContextState {
    cachedContext: any | null;
    isLoading: boolean;
    error: Error | null;
}

interface AppContextActions {
    prefetch: (token: string) => Promise<void>;
    hydrateStores: (data: any) => void;
    setupSyncListener: (uid: string, token: string) => void;
    cleanupSyncListener: () => void;
    reset: () => void;
}

export const useAppContextStore = create<AppContextState & AppContextActions>()(
    devtools(
        (set, get) => ({
            cachedContext: null,
            isLoading: false,
            error: null,

            hydrateStores: (data: any) => {
                if (!data) return;

                // Merge into cachedContext to avoid wiping out other data
                set((state) => ({
                    cachedContext: state.cachedContext ? { ...state.cachedContext, ...data } : data
                }));

                // 1. Profile Store (Atomic update)
                if (data.profile) {
                    const validTiers: UserTier[] = ['free', 'pro', 'premium', 'unlimited'];
                    const tier = validTiers.includes(data.profile.tier as UserTier) ? (data.profile.tier as UserTier) : 'free';

                    const isTrialActive = data.profile.trial_ends_at && new Date(data.profile.trial_ends_at) > new Date();
                    const isSommelierEntitled = tier === 'premium' || tier === 'unlimited' || isTrialActive;
                    const serverToggles = data.profile.toggles || { pantry: true, macro: true, wine: false };
                    const safeToggles = {
                        ...serverToggles,
                        wine: isSommelierEntitled ? serverToggles.wine : false
                    };

                    useProfileStore.setState({
                        userTier: tier,
                        toggles: safeToggles,
                        dietPhilosophy: data.profile.diet_philosophy,
                        macroProfile: data.profile.macro_profile,
                        wineSettings: data.profile.wine_pairing_settings,
                        mealSettings: data.profile.meal_structure_settings,
                        varietyMode: isValidVarietyMode(data.profile.variety_mode) ? data.profile.variety_mode : 'balanced',
                        loading: false
                    });

                    // Update Macro Store from profile data
                    useMacroStore.getState().setMacros({
                        calories: data.profile.calories || 2000,
                        protein: data.profile.protein || 150,
                        carbs: data.profile.carbs || 200,
                        fat: data.profile.fat || 65
                    });
                    useMacroStore.getState().setExclusions(data.profile.exclusions || []);
                }

                // 2. Inventory Store
                if (data.inventory) {
                    useInventoryStore.getState().setInventory(data.inventory);
                }

                // 3. Wine Store
                if (data.wine_cellar) {
                    useWineStore.getState().setWineCellar(data.wine_cellar);
                }

                // 5. Meal Plan Store (History and Quota)
                const mealPlanStore = useMealPlanStore.getState();
                if (data.recent_plans && mealPlanStore.setRecentPlans) {
                    mealPlanStore.setRecentPlans(data.recent_plans);
                }
                if (data.favorites && mealPlanStore.setFavorites) {
                    mealPlanStore.setFavorites(data.favorites);
                }
            },

            prefetch: async (token: string) => {
                set({ isLoading: true, error: null });
                try {
                    const data = await fetchAppContext(token);
                    get().hydrateStores(data);
                    console.log(`[AppContextStore] Prefetch successful. Fetched at ${data.fetched_at}`);
                } catch (err) {
                    console.error("[AppContextStore] Prefetch failed:", err);
                    set({ error: err instanceof Error ? err : new Error('Prefetch failed') });
                } finally {
                    set({ isLoading: false });
                }
            },

            setupSyncListener: (uid: string, token: string) => {
                const syncNode = `sync_events/${uid}/v1`;
                const syncRef = ref(database, syncNode);
                set({ syncRef } as any);

                console.log(`[AppContextStore] Setting up sync listener for ${uid}`);

                onValue(syncRef, (snapshot) => {
                    const data = snapshot.val();
                    if (!data) return;

                    // ECHO CANCELLATION: Check if this was caused by the current device
                    const myDeviceID = localStorage.getItem('v11_stable_device_id');
                    if (data.source_device_id === myDeviceID) {
                        return; // Ignore local changes
                    }

                    console.log(`[AppContextStore] Remote sync signal received (${data.affected_vessel}). Scheduling update...`);

                    // DEBOUNCE: Avoid multiple rapid refreshes
                    const state = get() as any;
                    if (state.debounceTimer) clearTimeout(state.debounceTimer);

                    const timer = setTimeout(() => {
                        console.log(`[AppContextStore] Executing debounced sync refresh`);
                        get().prefetch(token);
                    }, 1000);

                    set({ debounceTimer: timer } as any);
                });
            },

            cleanupSyncListener: () => {
                const state = get() as any;
                if (state.syncRef) {
                    off(state.syncRef);
                    set({ syncRef: null } as any);
                    console.log(`[AppContextStore] Sync listener detached.`);
                }
            },

            reset: () => {
                const state = get() as any;
                if (state.debounceTimer) clearTimeout(state.debounceTimer);
                set({ cachedContext: null, isLoading: false, error: null, debounceTimer: null } as any);
            }
        }),
        { name: 'AppContextStore' }
    )
);
